import { memo, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useStyles } from 'uinix-ui';

import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { Banner } from '~/packages/eds/src/components/Banner/Banner';
import {
  BannerAction,
  BannerStatus,
} from '~/packages/eds/src/components/Banner/types';
import { SharedLinkProps } from '~/packages/eds/src/types/SharedLinkProps';

import { STATUS_MESSAGE_AUTO_DISMISS_MS } from '../../constants/duration';
import { useElapse } from '../../hooks/useElapse';
import { useToggle } from '../../hooks/useToggle';
import { BaseStatusMessageProps } from '../../types/BaseStatusMessageProps';
import { IconType } from '../../types/IconType';
import { StatusType } from '../../types/StatusType';
import { UserAction } from '../../types/UserAction';
import { Actions } from '../Actions/Actions';
import { Box } from '../Box/Box';
import { CollapsibleText } from '../CollapsibleText/CollapsibleText';
import { Icon } from '../Icon/Icon';
import { Layout } from '../Layout/Layout';
import { Markdown } from '../Markdown/Markdown';
import { Text } from '../Text/Text';

interface Props extends BaseStatusMessageProps {
  /** aria-live to indicate the type of announcement of the live placeholder region */
  ariaLive?: 'assertive' | 'polite';
  /** Specifies the limit messsage length before using a CollapsibleText */
  limit?: number;
  /**
   * Optional supporting link. Rendered as a dedicated link on Eds2 and appended
   * to the message as markdown on Eds1, which has no link slot.
   */
  link?: SharedLinkProps;
  /** supported modes */
  mode?: 'banner' | 'note' | 'column';
}

const MAPPABLE_ACTION_LEVELS = new Set<UserAction['level']>([
  'action',
  'secondary',
  'subtle',
  'tertiary',
]);

export const StatusMessage = memo((props: Props) => {
  const useEds2 = useFlag(FlagType.EnableNewEdsStatusMessage);

  if (useEds2 && !hasEds1OnlyFeatures(props)) {
    return <StatusMessageEds2 {...props} />;
  }

  return <StatusMessageEds1 {...props} />;
});

function isUnmappableAction(action: UserAction) {
  // Safeguarding Only: no StatusMessage action sets mode. `link` is included —
  // Banner renders links through props[link], never as an action.
  if (action.mode) {
    return true;
  }
  // Safeguarding Only: no StatusMessage action sets an icon.
  // Safeguarding Only: no nested `actions` menu, no `count`.
  if (action.actions?.length || action.count != null || action.icon) {
    return true;
  }
  // Safeguarding Only: no iconPosition, no isDanger.
  if (action.iconPosition === 'right' || action.isDanger) {
    return true;
  }
  // Safeguarding Only: no pointer handlers.
  if (action.onMouseEnter || action.onMouseLeave) {
    return true;
  }
  // Safeguarding Only: no disabledTooltip / hash / pathname / state.
  if (
    action.disabledTooltip ||
    action.hash ||
    action.pathname ||
    action.state
  ) {
    return true;
  }
  // Safeguarding Only: no size other than default / s / m.
  if (action.size && action.size !== 's' && action.size !== 'm') {
    return true;
  }
  // Safeguarding Only: no primary / cta / danger / ai / *-active.
  if (action.level && !MAPPABLE_ACTION_LEVELS.has(action.level)) {
    return true;
  }

  // Navigation belongs on props[link], which renders on both paths. An href on
  // an action would become a link under Banner while Eds1 rendered a dead
  // button, since the 1.0 Button never emits an anchor.
  if (action.href) {
    return true;
  }

  return false;
}

function hasEds1OnlyFeatures({ action, autoDismiss, limit }: Props) {
  // Safeguarding Only: no StatusMessage callsite sets `limit` (Toast *can* via SharedToastProps).
  if (limit != null) {
    return true;
  }
  // Chat `{...statusMessage}` if a caller sets autoDismiss true.
  // Toast fan-in only if a toast passes autoDismiss: true.
  // ContractType passes autoDismiss={false} — that does not hit this branch.
  if (autoDismiss) {
    return true;
  }
  // Safeguarding Only: unmappable actions. Click-only actions map.
  if (action && isUnmappableAction(action)) {
    return true;
  }
  return false;
}

const BANNER_STATUS_BY_STATUS_TYPE = {
  active: 'informational',
  ai: 'informational',
  alert: 'critical',
  danger: 'critical',
  inactive: 'informational',
  info: 'informational',
  new: 'informational',
  success: 'positive',
  warning: 'cautious',
} as const satisfies Record<StatusType, BannerStatus>;

function mapBannerStatus(status: StatusType = 'info') {
  return BANNER_STATUS_BY_STATUS_TYPE[status];
}

function mapBannerAction(action: UserAction): BannerAction {
  return {
    // A handler-less action is inert by construction (the document processing
    // notice's Edit affordance), so Banner announces it as disabled rather
    // than rendering a control that silently does nothing.
    disabled: action.disabled || !action.onClick,
    isLoading: action.isLoading,
    label: action.text,
    onClick: action.onClick,
    text: action.text,
    tooltip: action.tooltip ? { text: action.tooltip } : undefined,
    variant: action.level === 'secondary' ? 'secondary' : 'tertiary',
  };
}

function StatusMessageEds2({
  action,
  link,
  message,
  status,
  title,
  onDismiss,
}: Props) {
  const [isVisible, _toggleIsVisible, _show, dismiss] = useToggle(true);

  const handleDismiss = useCallback(() => {
    onDismiss?.();
    dismiss();
  }, [dismiss, onDismiss]);

  if (!isVisible) {
    return null;
  }

  return (
    <Banner
      action={action ? mapBannerAction(action) : undefined}
      link={link}
      onDismiss={onDismiss ? handleDismiss : undefined}
      status={mapBannerStatus(status)}
      text={message}
      title={title}
    />
  );
}

function StatusMessageEds1({
  action,
  ariaLive,
  autoDismiss,
  limit,
  link,
  message,
  mode = 'note',
  status = 'info',
  title,
  onDismiss,
  ...rest
}: Props) {
  const [isVisible, _toggleIsVisible, _show, dismiss] = useToggle(true);
  const intl = useIntl();

  const handleDismiss = useCallback(() => {
    onDismiss?.();
    dismiss();
  }, [dismiss, onDismiss]);

  const actions = useMemo(() => {
    const actions: UserAction[] = [];
    if (action) {
      actions.push(action);
    }
    if (onDismiss) {
      actions.push({
        icon: 'x',
        mode: 'icon',
        text: intl.formatMessage({
          id: 'StatusMessage.dismiss',
          defaultMessage: 'Dismiss',
          description: 'Button label to dismiss a status message',
        }),
        tooltip: intl.formatMessage({
          id: 'StatusMessage.dismissTooltip',
          defaultMessage: 'Dismiss',
          description: 'Tooltip for dismissing a status message',
        }),
        onClick: handleDismiss,
      });
    }
    return actions;
  }, [action, handleDismiss, intl, onDismiss]);

  useElapse(STATUS_MESSAGE_AUTO_DISMISS_MS, {
    disabled: !autoDismiss,
    callback: handleDismiss,
  });

  const styles = useStyles();

  const markdownMessage = link
    ? `${message} [${link.text}](${link.href})`
    : message;

  if (!isVisible) {
    return null;
  }

  return (
    <Layout
      {...rest}
      align={mode === 'column' ? 'start' : 'center'}
      aria-live={ariaLive}
      border="border"
      borderRadius={mode === 'banner' ? undefined : 'm'}
      direction={mode === 'column' ? 'column' : 'row'}
      justify="space-between"
      px={4}
      py={3}
      role="alert"
      spacing={4}
      styles={styles.colors.status}
      styleProps={{ status }}
    >
      <Layout align="center" minW={0} spacing={2} styles={{ flex: 1 }}>
        <Icon icon={`status-${status}` as IconType} />
        <Layout direction="column" minW={0} styles={{ flex: 1 }}>
          {title && <Text variant="body-bold">{title}</Text>}
          {limit ? (
            <CollapsibleText
              limit={limit}
              text={message}
              whiteSpace="pre-wrap"
            />
          ) : (
            <Markdown text={markdownMessage} />
          )}
        </Layout>
      </Layout>
      {actions.length > 0 && (
        <Box
          ml={mode === 'column' ? 3 : undefined}
          styles={styles.sticky.top(4)}
        >
          <Actions actions={actions} />
        </Box>
      )}
    </Layout>
  );
}
